import { UNSAFE_DISPLAY_CHARS } from './unsafeCharacters.js';

/**
 * The one rule for the free text a member types — a name, a title, a note.
 *
 * #525 closed the disk half of this: a document's `filename` is validated on the way in
 * and repaired on the way out, so a file lands under a name that says what it is. The
 * title was deliberately left out of that scope, and every OTHER user-entered name in
 * the app was never in scope at all. `title`, `name`, `label`, `institutionName` and
 * their siblings are all still stored exactly as typed, so a right-to-left override in
 * an account name displays as a different account name, in the list, in the picker, in
 * the assistant's workspace snapshot and in the CSV export (steward-app#600).
 *
 * There is no code execution in this. It is deception only, and it needs a second
 * person in the workspace to be worth anything — which a family office or an advisor
 * arrangement routinely has, and which is the whole reason the vault has members at all.
 *
 * ## Strip, do not refuse
 *
 * The filename rule refuses at the API boundary, because the SPA cleans a filename
 * before it uploads, so an unrepaired name arriving at the API did not come from this
 * app. Display text is the opposite case. Nothing cleans it first, a member pastes from
 * Word and from a bank's PDF all day, and a soft hyphen riding along in a pasted
 * account name is not an attack — it is Tuesday. Refusing it would produce an edit that
 * cannot be saved and an error message about a character nobody can see. So this
 * strips, and the member keeps their name minus the parts that were never visible.
 *
 * ## Three passes, same as #525
 *
 *   - **On the way in**, so nothing new is stored dirty.
 *   - **On the way out**, because inbound validation does nothing for the rows already
 *     in the table, and every name stored before this existed was accepted unchecked.
 *     This is the pass that actually fixes today's data, and it needs no backfill.
 *   - **At the render sites**, where `<bdi>` isolates a user-entered name from the text
 *     around it. That third pass is not redundant: stripping kills the override
 *     characters, but a name written in an RTL script needs no override at all to
 *     reorder the punctuation and numbers beside it, and the SPA can be served a
 *     response from a React Query cache an older bundle filled.
 *
 * Two surfaces do not go through the façade's `toResource` and so are cleaned at their
 * own boundary instead: the assistant's workspace snapshot and tool results, which are
 * built from raw appcore instances (`assistant/context.ts`, `assistant/tools/index.ts`),
 * and the strings that leave the product entirely — an invite email's subject line, and
 * the browser tab title — which have no markup to isolate with and so are stripped.
 */

/**
 * The same text, with the characters that are never legitimate removed.
 *
 * Newline, carriage return and tab survive, and so do the two zero-width joiners
 * U+200C and U+200D; see `UNSAFE_DISPLAY_CHARS` for why each of them does. The
 * result is returned as the SAME string when nothing needed removing, so a caller can
 * cheaply tell whether anything changed.
 */
export function safeDisplayText(value: string): string {
  return value.replace(UNSAFE_DISPLAY_CHARS, '');
}

/**
 * Characters that render as nothing (or as blank space) but are not whitespace to `\s`, and that
 * `safeDisplayText` deliberately keeps or does not target: the zero-width space, non-joiner and
 * joiner (U+200B to U+200D), the word joiner (U+2060), the BOM (U+FEFF), the Hangul fillers
 * (U+115F, U+1160, U+3164, U+FFA0) and the blank Braille pattern (U+2800). Only the blankness
 * check uses this; a name that has other text keeps them (U+200D joins emoji sequences).
 */
const INVISIBLE_CHARS = /[\u200B-\u200D\u2060\uFEFF\u115F\u1160\u3164\uFFA0\u2800]/g;

/**
 * Whether `value` would show as nothing at all: empty after the characters `safeDisplayText`
 * removes, the invisible ones above and whitespace are all taken out. For a name a person types
 * (a chat title, steward-app#2622), where a blank one would render as an empty row.
 */
export function isBlankDisplayText(value: string): boolean {
  return safeDisplayText(value).replace(INVISIBLE_CHARS, '').trim() === '';
}

/** Whether `value` carries anything `safeDisplayText` would remove. */
export function hasUnsafeDisplayChars(value: string): boolean {
  return safeDisplayText(value) !== value;
}

/**
 * A workspace record's attributes, with every string in them cleaned.
 *
 * The attributes bag is the right granularity for this, and picking out named fields
 * instead would be the wrong one. `title` is only the field that happened to get
 * noticed; the bag also holds `name`, `label`, `lender`, `notes`, `category`, `tags`,
 * an address, an institution, and whatever a later feature adds. Walking the whole bag
 * means a field added next year inherits the rule by construction, which is the same
 * reason #525 ended up with one `FilenameInput` constant instead of three schemas.
 *
 * It walks nested objects and arrays because `tags` is an array of strings and the
 * property-detail fields nest. Non-strings pass through untouched, and the ORIGINAL
 * object is returned when nothing needed cleaning, so the ordinary read path — every
 * list response, every item in it — allocates nothing.
 *
 * A caveat worth stating rather than discovering: this must not be pointed at a bag
 * that holds a value whose exact bytes are the point. A password is not display text —
 * its bytes are what was meant, and quietly removing one would lock a member out of the
 * thing the credential unlocks. In this codebase the secret always travels BESIDE the
 * attributes rather than inside them (`credentials.ts`), which is what makes this safe
 * to apply to the bag as a whole; the same file's `ENVELOPE_KEYS` are deleted before
 * this is ever reached.
 *
 * The line is "is it read, or is it matched", not "is it sensitive". A credential's
 * `username` is in the bag and IS cleaned, correctly: it is a label a member reads off
 * the screen, not a value anything compares. `assetLots`' `source` and `externalId` are
 * the opposite case and are deliberately left out of the inbound schema there, because
 * `account-sync` matches rows on them.
 */
export function safeDisplayAttributes<T>(attributes: T): T {
  return walk(attributes, 0) as T;
}

/**
 * How far down this will follow a structure.
 *
 * A bound rather than a comment, because the input is a request body and this is the
 * first thing on the path that recurses. `JSON.parse` in V8 is iterative, so a member
 * can post a body nested tens of thousands deep inside the ordinary payload limit; a
 * `z.unknown()` waves it through, and an unbounded walk then throws a `RangeError` from
 * inside a Zod transform, which surfaces as an unexplained 500. Past the bound the value
 * is returned as it stands rather than descended into.
 *
 * Nothing real is close. The deepest attribute shape this app stores is a nested
 * property-detail object two or three levels down; 64 leaves room for a structure an
 * order of magnitude richer than anything anyone has written.
 */
const MAX_DEPTH = 64;

function walk(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return safeDisplayText(value);
  if (depth >= MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map(item => {
      const next = walk(item, depth + 1);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }

  // Plain objects only. A Date or any other class instance is left alone: rebuilding it
  // from its enumerable keys would return something that is no longer that class.
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      const next = walk(item, depth + 1);
      if (next !== item) changed = true;
      out[key] = next;
    }
    return changed ? out : value;
  }

  return value;
}

function isPlainObject(value: object): boolean {
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
